import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { isTrialValid, getTrialDaysRemaining, isSubscriptionValid, getSubscriptionDaysRemaining, getSpecialtyFields } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

const TrialBanner = (props) => {
    const history = useHistory();
    const [showBanner, setShowBanner] = useState(false);
    const [daysRemaining, setDaysRemaining] = useState(0);
    const [isExpired, setIsExpired] = useState(false);
    const [bannerType, setBannerType] = useState('trial'); // 'trial' or 'subscription'

    useEffect(() => {
        const userDetails = props.userDetails;
        const activeSpecialty = props.activeSpecialty || 'pmr';
        const fields = getSpecialtyFields(activeSpecialty);

        // Don't show for lifetime access users
        if (userDetails[fields.lifetimeAccess] === true || userDetails[fields.lifetimeAccess] === "true") {
            setShowBanner(false);
            return;
        }

        // Don't show for UAS students
        if (userDetails[fields.ultimateAdvisingStudent] === true || userDetails[fields.ultimateAdvisingStudent] === "true") {
            setShowBanner(false);
            return;
        }

        // Check subscription status first (one-time payment)
        const subEndDate = userDetails[fields.subscriptionEndDate];
        if (subEndDate) {
            const subValid = isSubscriptionValid(subEndDate);
            const remaining = getSubscriptionDaysRemaining(subEndDate);

            // Show banner if subscription is expiring soon (30 days) or expired
            if (subValid && remaining <= 30) {
                setDaysRemaining(remaining);
                setIsExpired(false);
                setBannerType('subscription');
                setShowBanner(true);
                return;
            } else if (!subValid) {
                setDaysRemaining(0);
                setIsExpired(true);
                setBannerType('subscription');
                setShowBanner(true);
                return;
            } else {
                // Active subscription with more than 30 days - no banner needed
                setShowBanner(false);
                return;
            }
        }

        // Check trial status
        const trialStart = userDetails[fields.trialStartDate];
        if (trialStart) {
            const trialValid = isTrialValid(trialStart);
            const remaining = getTrialDaysRemaining(trialStart);

            setDaysRemaining(remaining);
            setIsExpired(!trialValid);
            setBannerType('trial');

            // Show banner for active trial (discount urgency) or expired trial
            setShowBanner(true);
            return;
        }

        setShowBanner(false);
    }, [props.userDetails, props.activeSpecialty]);

    const handleUpgrade = () => {
        history.push('/client/plans');
    };

    if (!showBanner) return null;

    if (isExpired) {
        return (
            <Row className="mb-3">
                <Col lg="12">
                    <div className="alert alert-danger shadow mb-0" style={{ borderLeft: '4px solid #f5365c' }}>
                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <div>
                                <i className="fas fa-exclamation-circle text-danger mr-2"></i>
                                <strong>Your {bannerType === 'trial' ? 'free trial' : 'subscription'} has expired!</strong>{' '}
                                {bannerType === 'trial' ? 'Upgrade' : 'Extend your subscription'} now to continue accessing {getSpecialty(props.activeSpecialty).name} Prep.
                            </div>
                            <Button
                                color="danger"
                                size="sm"
                                className="ml-3 mt-2 mt-md-0"
                                onClick={handleUpgrade}
                            >
                                <i className="fas fa-crown mr-1"></i>
                                {bannerType === 'trial' ? 'Upgrade Now' : 'Extend Subscription'}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    // Active trial — show trial expiry urgency message
    if (bannerType === 'trial') {
        return (
            <Row className="mb-3">
                <Col lg="12">
                    <div className="alert alert-danger shadow mb-0" style={{ borderLeft: '4px solid #f5365c' }}>
                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                            <div>
                                <i className="fas fa-fire text-danger mr-2"></i>
                                <strong>Your free trial {daysRemaining === 0 ? 'ends today' : `ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`}!</strong>{' '}
                                Subscribe now to keep your access.
                            </div>
                            <Button
                                color="danger"
                                size="sm"
                                className="ml-3 mt-2 mt-md-0"
                                onClick={handleUpgrade}
                            >
                                <i className="fas fa-crown mr-1"></i>
                                View Plans
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    // Subscription expiring soon
    return (
        <Row className="mb-3">
            <Col lg="12">
                <div className="alert alert-warning shadow mb-0" style={{ borderLeft: '4px solid #fb6340' }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div>
                            <i className="fas fa-clock text-warning mr-2"></i>
                            <strong>Subscription:</strong>{' '}
                            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining.
                        </div>
                        <Button
                            color="warning"
                            size="sm"
                            className="ml-3 mt-2 mt-md-0"
                            onClick={handleUpgrade}
                        >
                            <i className="fas fa-crown mr-1"></i>
                            Extend Subscription
                        </Button>
                    </div>
                </div>
            </Col>
        </Row>
    );
};

const mapStateToProps = state => {
    return {
        userDetails: state.loginAuth.userDetails,
        fetchDetailsSubscription: state.fetchDetailsSubscription,
        activeSpecialty: state.specialty.activeSpecialty,
    };
};

export default connect(mapStateToProps)(TrialBanner);
